import { useState, useCallback, useRef, useEffect } from 'react';

/**
 * Hook personnalisé pour gérer l'undo/redo des actions WebSocket
 * 
 * Chaque action stockée contient:
 * - actionType: le type d'action (ex: 'position_update', 'dancer_create')
 * - undoData: les données nécessaires pour annuler l'action
 * - redoData: les données nécessaires pour refaire l'action
 * - description: description lisible de l'action
 */

const MAX_HISTORY_SIZE = 100; // Limite pour éviter une consommation mémoire excessive

export function useUndoRedo(wsSend, accessToken) {
  // Piles d'historique
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  
  // Ref pour éviter les boucles infinies lors des undo/redo
  const isUndoRedoInProgress = useRef(false);
  // Ref pour savoir si on est dans un undo (true) ou redo (false)
  const isUndoOperation = useRef(false);
  // Ref pour stocker le timestamp à restaurer lors d'un undo/redo
  const restoreTimeRef = useRef(null);
  // Ref pour stocker l'ID de la période à reselectioner lors d'un undo/redo
  const restorePeriodIdRef = useRef(null);
  
  // Ref pour accéder aux valeurs actuelles dans les callbacks
  const undoStackRef = useRef(undoStack);
  const redoStackRef = useRef(redoStack);
  const wsSendRef = useRef(wsSend);
  const accessTokenRef = useRef(accessToken);
  
  useEffect(() => {
    undoStackRef.current = undoStack;
  }, [undoStack]);
  
  useEffect(() => {
    redoStackRef.current = redoStack;
  }, [redoStack]);
  
  useEffect(() => {
    wsSendRef.current = wsSend;
  }, [wsSend]);
  
  useEffect(() => {
    accessTokenRef.current = accessToken;
  }, [accessToken]);

  /**
   * Enregistre une action dans l'historique
   * Appelé APRÈS que l'action ait été exécutée avec succès
   */
  const recordAction = useCallback((actionType, undoData, redoData, description = '') => {
    // Ne pas enregistrer si on est en train de faire un undo/redo
    if (isUndoRedoInProgress.current) {
      return;
    }
    
    const action = {
      actionType,
      undoData,
      redoData,
      description,
      timestamp: Date.now()
    };
    
    setUndoStack(prev => {
      const newStack = [...prev, action];
      // Limiter la taille de l'historique
      if (newStack.length > MAX_HISTORY_SIZE) {
        return newStack.slice(-MAX_HISTORY_SIZE);
      }
      return newStack;
    });
    
    // Vider la pile redo quand une nouvelle action est effectuée
    setRedoStack([]);
  }, []);

  /**
   * Enregistre une action composite (batch) dans l'historique
   * Permet de regrouper plusieurs actions en une seule pour le undo/redo
   * @param {Array} actions - Tableau d'objets { undoData, redoData }
   * @param {string} description - Description de l'action composite
   */
  const recordBatchAction = useCallback((actions, description = '') => {
    // Ne pas enregistrer si on est en train de faire un undo/redo
    if (isUndoRedoInProgress.current) {
      return;
    }
    
    if (!actions || actions.length === 0) return;
    
    const batchAction = {
      actionType: 'batch',
      undoData: {
        actionType: 'batch',
        actions: actions.map(a => a.undoData)
      },
      redoData: {
        actionType: 'batch',
        actions: actions.map(a => a.redoData)
      },
      description,
      timestamp: Date.now()
    };
    
    setUndoStack(prev => {
      const newStack = [...prev, batchAction];
      if (newStack.length > MAX_HISTORY_SIZE) {
        return newStack.slice(-MAX_HISTORY_SIZE);
      }
      return newStack;
    });
    
    // Vider la pile redo quand une nouvelle action est effectuée
    setRedoStack([]);
  }, []);

  /**
   * Exécute une action WebSocket (utilisé pour undo et redo)
   * Gère aussi les actions batch (composite)
   */
  const executeAction = useCallback(async (actionData) => {
    const { actionType, data, actions } = actionData;
    
    if (!wsSendRef.current) {
      console.error('WebSocket non disponible pour undo/redo');
      return false;
    }
    
    try {
      // Si c'est une action batch, exécuter toutes les sous-actions
      if (actionType === 'batch' && actions) {
        for (const subAction of actions) {
          const wsMessage = {
            ...subAction.data,
            type: subAction.actionType,
            request_id: `undo_redo_${Date.now()}_${Math.random()}`
          };
          wsSendRef.current(wsMessage);
          // Petit délai entre les actions pour éviter les problèmes de concurrence
          await new Promise(resolve => setTimeout(resolve, 50));
        }
        return true;
      }
      
      // Action simple
      const wsMessage = {
        ...data,
        type: actionType,
        request_id: `undo_redo_${Date.now()}_${Math.random()}`
      };
      
      wsSendRef.current(wsMessage);
      return true;
    } catch (error) {
      console.error('Erreur lors de l\'exécution undo/redo:', error);
      return false;
    }
  }, []);

  /**
   * Annule la dernière action
   */
  const undo = useCallback(async () => {
    const currentUndoStack = undoStackRef.current;
    
    if (currentUndoStack.length === 0) {
      return false;
    }
    
    isUndoRedoInProgress.current = true;
    isUndoOperation.current = true; // ✅ On est dans un undo
    
    try {
      const lastAction = currentUndoStack[currentUndoStack.length - 1];
      
      // Stocker le timestamp et la période à restaurer s'ils existent
      // Pour les actions batch, on prend les valeurs de la première sous-action
      if (lastAction.undoData.actionType === 'batch' && lastAction.undoData.actions?.length > 0) {
        const firstSubAction = lastAction.undoData.actions[0];
        restoreTimeRef.current = firstSubAction.data?._restore_time ?? null;
        restorePeriodIdRef.current = firstSubAction.data?._restore_period_id ?? null;
      } else {
        restoreTimeRef.current = lastAction.undoData.data?._restore_time ?? null;
        restorePeriodIdRef.current = lastAction.undoData.data?._restore_period_id ?? null;
      }
      
      // Exécuter l'action d'annulation
      const success = await executeAction({
        actionType: lastAction.undoData.actionType,
        data: lastAction.undoData.data,
        actions: lastAction.undoData.actions // Pour les batch
      });
      
      if (success) {
        // Déplacer l'action vers la pile redo
        setUndoStack(prev => prev.slice(0, -1));
        setRedoStack(prev => [...prev, lastAction]);
      }
      
      return success;
    } finally {
      // Petit délai pour éviter que les messages WebSocket entrants ne soient enregistrés
      setTimeout(() => {
        isUndoRedoInProgress.current = false;
        isUndoOperation.current = false;
        restoreTimeRef.current = null;
        restorePeriodIdRef.current = null;
      }, 150); // Augmenté pour les batch
    }
  }, [executeAction]);

  /**
   * Refait la dernière action annulée
   * Retourne l'action qui a été refaite (pour pouvoir mettre à jour son undoData si besoin)
   */
  const redo = useCallback(async () => {
    const currentRedoStack = redoStackRef.current;
    
    if (currentRedoStack.length === 0) {
      return { success: false, action: null };
    }
    
    isUndoRedoInProgress.current = true;
    isUndoOperation.current = false; // ✅ On est dans un redo
    
    try {
      const lastUndoneAction = currentRedoStack[currentRedoStack.length - 1];
      
      // Stocker le timestamp et la période à restaurer s'ils existent
      // Pour les actions batch, on prend les valeurs de la première sous-action
      if (lastUndoneAction.redoData.actionType === 'batch' && lastUndoneAction.redoData.actions?.length > 0) {
        const firstSubAction = lastUndoneAction.redoData.actions[0];
        restoreTimeRef.current = firstSubAction.data?._restore_time ?? null;
        restorePeriodIdRef.current = firstSubAction.data?._restore_period_id ?? null;
      } else {
        restoreTimeRef.current = lastUndoneAction.redoData.data?._restore_time ?? null;
        restorePeriodIdRef.current = lastUndoneAction.redoData.data?._restore_period_id ?? null;
      }
      
      // Exécuter l'action de redo
      const success = await executeAction({
        actionType: lastUndoneAction.redoData.actionType,
        data: lastUndoneAction.redoData.data,
        actions: lastUndoneAction.redoData.actions // Pour les batch
      });
      
      if (success) {
        // Déplacer l'action vers la pile undo
        setRedoStack(prev => prev.slice(0, -1));
        setUndoStack(prev => [...prev, lastUndoneAction]);
      }
      
      return { success, action: lastUndoneAction };
    } finally {
      setTimeout(() => {
        isUndoRedoInProgress.current = false;
        isUndoOperation.current = false;
        restoreTimeRef.current = null;
        restorePeriodIdRef.current = null;
      }, 150); // Augmenté pour les batch
    }
  }, [executeAction]);

  /**
   * Met à jour les données de la dernière action dans le stack undo
   * Utilisé après un redo de création pour mettre à jour l'ID de la nouvelle entité
   */
  const updateLastUndoAction = useCallback((updateFn) => {
    setUndoStack(prev => {
      if (prev.length === 0) return prev;
      const lastIndex = prev.length - 1;
      const updatedAction = updateFn(prev[lastIndex]);
      return [...prev.slice(0, lastIndex), updatedAction];
    });
  }, []);

  /**
   * Met à jour les données de la dernière action dans le stack redo
   * Utilisé après un undo qui recrée une entité pour mettre à jour l'ID dans redoData
   */
  const updateLastRedoAction = useCallback((updateFn) => {
    setRedoStack(prev => {
      if (prev.length === 0) return prev;
      const lastIndex = prev.length - 1;
      const updatedAction = updateFn(prev[lastIndex]);
      return [...prev.slice(0, lastIndex), updatedAction];
    });
  }, []);

  /**
   * Vide tout l'historique
   */
  const clearHistory = useCallback(() => {
    setUndoStack([]);
    setRedoStack([]);
  }, []);

  /**
   * Vérifie si on est en train de faire un undo/redo
   * (pour éviter d'enregistrer les actions résultantes)
   */
  const isInUndoRedo = useCallback(() => {
    return isUndoRedoInProgress.current;
  }, []);

  /**
   * Vérifie si on est dans un undo (true) ou redo (false)
   * Utile pour savoir quel stack mettre à jour après une création
   */
  const isUndo = useCallback(() => {
    return isUndoOperation.current;
  }, []);

  /**
   * Récupère le timestamp à restaurer lors d'un undo/redo
   * Retourne null si pas de timestamp à restaurer
   */
  const getRestoreTime = useCallback(() => {
    return restoreTimeRef.current;
  }, []);

  /**
   * Récupère l'ID de la période à resélectionner lors d'un undo/redo
   * Retourne null si pas de période à restaurer
   */
  const getRestorePeriodId = useCallback(() => {
    return restorePeriodIdRef.current;
  }, []);

  return {
    // État
    canUndo: undoStack.length > 0,
    canRedo: redoStack.length > 0,
    undoStackSize: undoStack.length,
    redoStackSize: redoStack.length,
    
    // Actions
    recordAction,
    recordBatchAction,
    undo,
    redo,
    clearHistory,
    isInUndoRedo,
    isUndo,
    getRestoreTime,
    getRestorePeriodId,
    updateLastUndoAction,
    updateLastRedoAction
  };
}

/**
 * Helpers pour créer les données undo/redo pour chaque type d'action
 */
export const UndoRedoHelpers = {
  // ==================== POSITIONS ====================
  
  positionUpdate: (positionId, oldData, newData) => ({
    undoData: {
      actionType: 'position_update',
      data: { position_id: positionId, ...oldData }
    },
    redoData: {
      actionType: 'position_update',
      data: { position_id: positionId, ...newData }
    },
    description: 'Déplacement de position'
  }),
  
  positionCreate: (position) => ({
    undoData: {
      actionType: 'position_delete',
      data: { position_id: position.id }
    },
    redoData: {
      actionType: 'position_create',
      data: { 
        period_id: position.period_id,
        x: position.x,
        y: position.y,
        dancer_id: position.dancer_id
      }
    },
    description: 'Création de position'
  }),
  
  positionDelete: (position) => ({
    undoData: {
      actionType: 'position_create',
      data: {
        period_id: position.period_id,
        x: position.x,
        y: position.y,
        dancer_id: position.dancer_id,
        // Stocker les skills et stunts pour restauration complète
        _restore_skills: position.skills?.map(s => s.id) || [],
        _restore_stunts: position.stunts?.map(s => s.id) || [],
        _restore_stunt_instance_id: position.stunt_instance_id
      }
    },
    redoData: {
      actionType: 'position_delete',
      data: { position_id: position.id }
    },
    description: 'Suppression de position'
  }),
  
  positionAssignDancer: (positionId, oldDancerId, newDancerId) => ({
    undoData: {
      actionType: 'position_assign_dancer',
      data: { position_id: positionId, dancer_id: oldDancerId }
    },
    redoData: {
      actionType: 'position_assign_dancer',
      data: { position_id: positionId, dancer_id: newDancerId }
    },
    description: newDancerId ? 'Assignation de danseur' : 'Désassignation de danseur'
  }),
  
  // ==================== DANCERS ====================
  
  dancerCreate: (dancer, sessionId) => ({
    undoData: {
      actionType: 'dancer_delete',
      data: { dancer_id: dancer.id }
    },
    redoData: {
      actionType: 'dancer_create',
      data: { session_id: sessionId, name: dancer.name }
    },
    description: `Création du danseur "${dancer.name}"`
  }),
  
  dancerDelete: (dancer, sessionId) => ({
    undoData: {
      actionType: 'dancer_create',
      data: { 
        session_id: sessionId, 
        name: dancer.name,
        _restore_skills: dancer.skills?.map(s => s.id) || []
      }
    },
    redoData: {
      actionType: 'dancer_delete',
      data: { dancer_id: dancer.id }
    },
    description: `Suppression du danseur "${dancer.name}"`
  }),
  
  // ==================== SKILLS ====================
  
  skillCreate: (skill, sessionId) => ({
    undoData: {
      actionType: 'skill_delete',
      data: { skill_id: skill.id }
    },
    redoData: {
      actionType: 'skill_create',
      data: { session_id: sessionId, name: skill.name, color: skill.color }
    },
    description: `Création du skill "${skill.name}"`
  }),
  
  skillDelete: (skill, sessionId) => ({
    undoData: {
      actionType: 'skill_create',
      data: { session_id: sessionId, name: skill.name, color: skill.color }
    },
    redoData: {
      actionType: 'skill_delete',
      data: { skill_id: skill.id }
    },
    description: `Suppression du skill "${skill.name}"`
  }),
  
  // ==================== PERIODS ====================
  
  periodCreate: (period, sessionId) => ({
    undoData: {
      actionType: 'period_delete',
      data: { period_id: period.id }
    },
    redoData: {
      actionType: 'period_create',
      data: { 
        session_id: sessionId, 
        name: period.name,
        start_time: period.start_time,
        end_time: period.end_time
      }
    },
    description: `Création de la période "${period.name}"`
  }),
  
  periodUpdate: (periodId, oldData, newData) => ({
    undoData: {
      actionType: 'period_update',
      data: { period_id: periodId, ...oldData }
    },
    redoData: {
      actionType: 'period_update',
      data: { period_id: periodId, ...newData }
    },
    description: 'Modification de période'
  }),
  
  periodDelete: (period, sessionId) => ({
    undoData: {
      actionType: 'period_create',
      data: { 
        session_id: sessionId, 
        name: period.name,
        start_time: period.start_time,
        end_time: period.end_time,
        _restore_positions: period.positions || []
      }
    },
    redoData: {
      actionType: 'period_delete',
      data: { period_id: period.id }
    },
    description: `Suppression de la période "${period.name}"`
  }),
  
  // ==================== SKILL ASSOCIATIONS ====================
  
  skillAssociateToPosition: (positionId, skillId, sessionId) => ({
    undoData: {
      actionType: 'position_remove_skill',
      data: { position_id: positionId, skill_id: skillId, session_id: sessionId }
    },
    redoData: {
      actionType: 'position_associate_skill',
      data: { position_id: positionId, skill_id: skillId, session_id: sessionId }
    },
    description: 'Association skill à position'
  }),
  
  skillRemoveFromPosition: (positionId, skillId, sessionId) => ({
    undoData: {
      actionType: 'position_associate_skill',
      data: { position_id: positionId, skill_id: skillId, session_id: sessionId }
    },
    redoData: {
      actionType: 'position_remove_skill',
      data: { position_id: positionId, skill_id: skillId, session_id: sessionId }
    },
    description: 'Dissociation skill de position'
  }),
  
  skillAssociateToDancer: (dancerId, skillId, sessionId) => ({
    undoData: {
      actionType: 'dancer_remove_skill',
      data: { dancer_id: dancerId, skill_id: skillId, session_id: sessionId }
    },
    redoData: {
      actionType: 'dancer_associate_skill',
      data: { dancer_id: dancerId, skill_id: skillId, session_id: sessionId }
    },
    description: 'Association skill à danseur'
  }),
  
  skillRemoveFromDancer: (dancerId, skillId, sessionId) => ({
    undoData: {
      actionType: 'dancer_associate_skill',
      data: { dancer_id: dancerId, skill_id: skillId, session_id: sessionId }
    },
    redoData: {
      actionType: 'dancer_remove_skill',
      data: { dancer_id: dancerId, skill_id: skillId, session_id: sessionId }
    },
    description: 'Dissociation skill de danseur'
  }),
  
  // ==================== STUNT ASSOCIATIONS ====================
  
  stuntAssociateToPosition: (positionId, stuntId, sessionId, stuntInstanceId = null) => ({
    undoData: {
      actionType: 'position_remove_stunt',
      data: { position_id: positionId, stunt_id: stuntId, session_id: sessionId }
    },
    redoData: {
      actionType: 'position_associate_stunt',
      data: { position_id: positionId, stunt_id: stuntId, session_id: sessionId, stunt_instance_id: stuntInstanceId }
    },
    description: 'Association stunt à position'
  }),
  
  stuntRemoveFromPosition: (positionId, stuntId, sessionId) => ({
    undoData: {
      actionType: 'position_associate_stunt',
      data: { position_id: positionId, stunt_id: stuntId, session_id: sessionId }
    },
    redoData: {
      actionType: 'position_remove_stunt',
      data: { position_id: positionId, stunt_id: stuntId, session_id: sessionId }
    },
    description: 'Dissociation stunt de position'
  }),
  
  // ==================== LINKS ====================
  
  linkCreate: (linkGroupId, positionIds, sessionId) => ({
    undoData: {
      actionType: 'link_delete',
      data: { link_group_id: linkGroupId, session_id: sessionId }
    },
    redoData: {
      actionType: 'link_create',
      data: { position_ids: positionIds, session_id: sessionId }
    },
    description: 'Création de lien'
  }),
  
  linkDelete: (linkGroupId, positionIds, sessionId) => ({
    undoData: {
      actionType: 'link_create',
      data: { position_ids: positionIds, session_id: sessionId }
    },
    redoData: {
      actionType: 'link_delete',
      data: { link_group_id: linkGroupId, session_id: sessionId }
    },
    description: 'Suppression de lien'
  }),
  
  // ==================== STUNTS CRUD ====================
  
  stuntCreate: (stunt, sessionId) => ({
    undoData: {
      actionType: 'stunt_delete',
      data: { stunt_id: stunt.id, session_id: sessionId }
    },
    redoData: {
      actionType: 'stunt_create',
      data: { session_id: sessionId, name: stunt.name, color: stunt.color }
    },
    description: `Création du stunt "${stunt.name}"`
  }),
  
  stuntDelete: (stunt, sessionId) => ({
    undoData: {
      actionType: 'stunt_create',
      data: { session_id: sessionId, name: stunt.name, color: stunt.color }
    },
    redoData: {
      actionType: 'stunt_delete',
      data: { stunt_id: stunt.id, session_id: sessionId }
    },
    description: `Suppression du stunt "${stunt.name}"`
  }),
  
  // ==================== STUNT GROUPS ====================
  
  stuntGroupCreate: (stuntGroup, sessionId) => ({
    undoData: {
      actionType: 'stunt_group_delete',
      data: { group_id: stuntGroup.id, session_id: sessionId }
    },
    redoData: {
      actionType: 'stunt_group_create',
      data: { session_id: sessionId, name: stuntGroup.name }
    },
    description: `Création du groupe "${stuntGroup.name}"`
  }),
  
  stuntGroupDelete: (stuntGroup, sessionId) => ({
    undoData: {
      actionType: 'stunt_group_create',
      data: { 
        session_id: sessionId, 
        name: stuntGroup.name,
        _restore_dancers: stuntGroup.dancers?.map(d => d.id) || [],
        _restore_stunts: stuntGroup.stunts?.map(s => s.id) || []
      }
    },
    redoData: {
      actionType: 'stunt_group_delete',
      data: { group_id: stuntGroup.id, session_id: sessionId }
    },
    description: `Suppression du groupe "${stuntGroup.name}"`
  }),
  
  stuntGroupAddDancers: (groupId, oldDancerIds, newDancerIds, sessionId) => ({
    undoData: {
      actionType: 'stunt_group_add_dancers',
      data: { group_id: groupId, dancer_ids: oldDancerIds, session_id: sessionId }
    },
    redoData: {
      actionType: 'stunt_group_add_dancers',
      data: { group_id: groupId, dancer_ids: newDancerIds, session_id: sessionId }
    },
    description: 'Modification des danseurs du groupe'
  }),
  
  stuntGroupAddStunts: (groupId, oldStuntIds, newStuntIds, sessionId) => ({
    undoData: {
      actionType: 'stunt_group_add_stunts',
      data: { group_id: groupId, stunt_ids: oldStuntIds, session_id: sessionId }
    },
    redoData: {
      actionType: 'stunt_group_add_stunts',
      data: { group_id: groupId, stunt_ids: newStuntIds, session_id: sessionId }
    },
    description: 'Modification des stunts du groupe'
  }),
  
  // ==================== UPDATES (NAME/COLOR/ETC) ====================
  
  dancerUpdate: (dancerId, oldData, newData) => ({
    undoData: {
      actionType: 'dancer_update',
      data: { dancer_id: dancerId, ...oldData }
    },
    redoData: {
      actionType: 'dancer_update',
      data: { dancer_id: dancerId, ...newData }
    },
    description: 'Modification du danseur'
  }),
  
  skillUpdate: (skillId, oldData, newData) => ({
    undoData: {
      actionType: 'skill_update',
      data: { skill_id: skillId, ...oldData }
    },
    redoData: {
      actionType: 'skill_update',
      data: { skill_id: skillId, ...newData }
    },
    description: 'Modification du skill'
  }),
  
  stuntUpdate: (stuntId, oldData, newData) => ({
    undoData: {
      actionType: 'stunt_update',
      data: { stunt_id: stuntId, ...oldData }
    },
    redoData: {
      actionType: 'stunt_update',
      data: { stunt_id: stuntId, ...newData }
    },
    description: 'Modification du stunt'
  }),
  
  stuntGroupUpdate: (groupId, oldData, newData) => ({
    undoData: {
      actionType: 'stunt_group_update',
      data: { group_id: groupId, ...oldData }
    },
    redoData: {
      actionType: 'stunt_group_update',
      data: { group_id: groupId, ...newData }
    },
    description: 'Modification du groupe'
  }),
  
  // ==================== LINK UNLINK POSITION ====================
  
  linkUnlinkPosition: (positionId, oldLinkGroupId, sessionId) => ({
    undoData: {
      // Pour undo, on recrée le lien - mais on a besoin des autres positions du groupe
      // Ceci est complexe car on perd les autres positions quand on unlink
      // On stocke juste le link_group_id pour référence
      actionType: 'position_update',
      data: { position_id: positionId, link_group_id: oldLinkGroupId }
    },
    redoData: {
      actionType: 'link_unlink_position',
      data: { position_id: positionId, session_id: sessionId }
    },
    description: 'Déliaison de position'
  }),
  
  // ==================== SESSION GRID ====================
  
  sessionUpdateGrid: (oldColumns, oldRows, newColumns, newRows) => ({
    undoData: {
      actionType: 'session_update_grid',
      data: { grid_columns: oldColumns, grid_rows: oldRows }
    },
    redoData: {
      actionType: 'session_update_grid',
      data: { grid_columns: newColumns, grid_rows: newRows }
    },
    description: 'Modification de la grille'
  }),
};

export default useUndoRedo;
